import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Clock, Loader2, Upload } from "lucide-react";
import { Reveal, Hud } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", phone: "", email: "", address: "", description: "", preferred_date: "" };

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (photo) fd.append("photo", photo);
      await axios.post(`${API}/quotes`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Request received. We'll call or text you shortly.");
      setForm(EMPTY);
      setPhoto(null);
    } catch {
      toast.error("Something went wrong. Please call or text (470) 684-0150.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative border-t-2 border-gold/30">
      <div className="grid grid-cols-1 lg:grid-cols-2">
        <div className="relative flex flex-col justify-center overflow-hidden bg-ink p-8 md:p-12 lg:min-h-full">
          <div className="hazard absolute left-0 top-0 h-full w-2.5" aria-hidden="true" />
          <Reveal className="pl-4">
            <h2 data-testid="contact-motto" aria-label="We move fast. We move right." className="font-heading uppercase leading-[0.88] tracking-tight">
              <span className="block text-6xl text-white md:text-8xl">We Move</span>
              <span className="block text-6xl text-white md:text-8xl">Fast<span className="text-gold">.</span></span>
              <span className="mt-3 block text-6xl text-white md:text-8xl">We Move</span>
              <span className="block text-6xl text-gold md:text-8xl">Right.</span>
            </h2>
            <p data-testid="contact-tagline" className="mt-6 font-scripture text-2xl italic text-gold md:text-3xl">
              Built On Faith! Built To Last!
            </p>
            <p className="mt-6 max-w-md text-base font-medium capitalize leading-relaxed text-warmgray">
              One call or one form — that's all it takes. We handle the lifting, the loading, and the sweep-up after.
            </p>
            <div className="mt-8 space-y-3">
              <a href="tel:4706840150" data-testid="contact-call-chip" className="flex w-fit items-center gap-3 border-2 border-gold/60 bg-panel px-5 py-3 font-body text-sm font-bold uppercase tracking-[0.15em] text-white transition-colors duration-200 hover:border-gold hover:text-gold">
                <Phone size={16} className="text-gold" /> (470) 684-0150
              </a>
              <a href="mailto:info@godspeedjunkhauling.com" data-testid="contact-email-chip" className="flex w-fit items-center gap-3 border-2 border-coal bg-panel px-5 py-3 font-body text-sm font-bold text-white/85 transition-colors duration-200 hover:border-gold hover:text-gold">
                <Mail size={16} className="text-gold" /> info@godspeedjunkhauling.com
              </a>
              <p className="flex items-center gap-3 px-1 font-body text-sm font-bold uppercase tracking-[0.15em] text-white/70">
                <Clock size={16} className="text-gold" /> Mon–Sat · 9AM–7PM
              </p>
            </div>
          </Reveal>
        </div>

        <div className="bg-panel px-5 py-16 md:px-12 md:py-20">
          <Reveal>
            <Hud>Free Quote</Hud>
            <p className="mt-4 max-w-md text-sm font-medium capitalize leading-relaxed text-warmgray md:text-base">
              No obligation. No pressure. Tell us what's got to go and we'll come back with an honest price.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="quote-form" className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
              <label className="block">
                <span className="mb-2 block font-body text-xs font-bold uppercase tracking-[0.2em] text-gold">Name</span>
                <input required data-testid="quote-name-input" value={form.name} onChange={set("name")} placeholder="Your name" className="input-brutal" />
              </label>
              <label className="block">
                <span className="mb-2 block font-body text-xs font-bold uppercase tracking-[0.2em] text-gold">Phone</span>
                <input required type="tel" data-testid="quote-phone-input" value={form.phone} onChange={set("phone")} placeholder="(470) 000-0000" className="input-brutal" />
              </label>
              <label className="block">
                <span className="mb-2 block font-body text-xs font-bold uppercase tracking-[0.2em] text-gold">Email</span>
                <input required type="email" data-testid="quote-email-input" value={form.email} onChange={set("email")} placeholder="you@email.com" className="input-brutal" />
              </label>
              <label className="block">
                <span className="mb-2 block font-body text-xs font-bold uppercase tracking-[0.2em] text-gold">Service Address / City</span>
                <input required data-testid="quote-address-input" value={form.address} onChange={set("address")} placeholder="Where's the junk?" className="input-brutal" />
              </label>
              <label className="block sm:col-span-2">
                <span className="mb-2 block font-body text-xs font-bold uppercase tracking-[0.2em] text-gold">What needs hauling?</span>
                <textarea required rows={4} data-testid="quote-description-input" value={form.description} onChange={set("description")} placeholder="Garage cleanout, old furniture, yard debris..." className="input-brutal" />
              </label>
              <label className="block">
                <span className="mb-2 block font-body text-xs font-bold uppercase tracking-[0.2em] text-gold">Preferred Date (optional)</span>
                <input type="date" data-testid="quote-date-input" value={form.preferred_date} onChange={set("preferred_date")} className="input-brutal [color-scheme:dark]" />
              </label>
              <label className="block cursor-pointer">
                <span className="mb-2 block font-body text-xs font-bold uppercase tracking-[0.2em] text-gold">Photo (optional)</span>
                <input type="file" accept="image/*" data-testid="quote-photo-input" className="hidden" onChange={(e) => setPhoto(e.target.files[0] || null)} />
                <span className="flex h-[52px] items-center justify-center gap-2 border border-dashed border-gold/50 bg-[#1B1B1E] px-4 font-body text-xs font-bold uppercase tracking-[0.15em] text-white/70 transition-colors duration-200 hover:border-gold hover:text-gold">
                  <Upload size={14} className="text-gold" />
                  {photo ? photo.name : "Add a photo of the junk"}
                </span>
              </label>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="quote-submit-button"
                  className="btn-brutal shadow-hard-gold flex w-full items-center justify-center gap-3 bg-gold px-10 py-4 font-heading text-2xl uppercase tracking-wide text-ink disabled:opacity-60"
                >
                  {loading && <Loader2 size={18} className="animate-spin" />}
                  {loading ? "Sending..." : "Get My Free Quote"}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
